import {
  EDLMaterial,
  OpacityMaterial,
  EDLRenderTarget,
  OpacityRenderTarget
} from ".";
import {
  AmbientLight,
  Camera,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneBufferGeometry,
  Scene,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget
} from "three";

export class RenderPass {
  private readonly screenScene: Scene;
  private readonly screenQuad: Mesh;
  private readonly camera: Camera;

  constructor() {
    const geometry = new PlaneBufferGeometry(2, 2);
    const material = new MeshBasicMaterial({
      depthTest: true,
      depthWrite: true,
      transparent: true
    });

    this.screenScene = new Scene();
    this.screenQuad = new Mesh(geometry, material);
    this.screenScene.add(this.screenQuad);
    this.camera = new Camera();
  }

  /** Render to screen. Note that our render target is changed after using this function */
  render(renderer, material, target = null) {
    this.screenQuad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this.screenScene, this.camera);
  }
}

export class RenderUtils {
  private renderer: WebGLRenderer;
  private camera: PerspectiveCamera;
  private screenPass: RenderPass;
  private targets: WebGLRenderTarget[];
  private pixelRatio;
  private opacityMaterial: OpacityMaterial;
  private opacityRenderTarget: OpacityRenderTarget;
  private EDLMaterial: EDLMaterial;
  private EDLRenderTarget: EDLRenderTarget;

  constructor(renderer, camera, pixelRatio=1.0) {
    this.renderer = renderer;
    this.camera = camera;
    this.screenPass = new RenderPass();
    this.targets = [];
    this.pixelRatio = pixelRatio;

    this.initTargets();

    if (this.isWebGL2) {
      console.log("WebGL2 Detected");
    }
  }

  get renderTargetAttrs() {
    return {
      tDepth: this.renderTarget.depthTexture,
      colorMap: this.renderTarget.texture,
    };
  }

  get EDLSupport() : boolean {
    let capabilities = this.renderer.capabilities;
    let supported = (capabilities.maxVaryings > 8)
      && capabilities.floatFragmentTextures;

    return supported;
  }

  get renderTarget() {
    return this.EDLSupport
      ? this.EDLRenderTarget
      : this.opacityRenderTarget;
  }

  get renderMaterial() {
    return this.EDLSupport
      ? this.EDLMaterial
      : this.opacityMaterial;
  }

  get isWebGL2() {
    let capabilities = this.renderer.capabilities;
    return capabilities.isWebGL2;
  }

  get width() {
    let renderSize = new Vector2();
    this.renderer.getSize(renderSize);
    return renderSize.x;
  }

  get height() {
    let renderSize = new Vector2();
    this.renderer.getSize(renderSize);
    return renderSize.y;
  }

  initTargets() {
    this.opacityMaterial = new OpacityMaterial();
    this.opacityRenderTarget = new OpacityRenderTarget(this.width, this.height);
    this.addTarget(this.opacityRenderTarget);

    if (this.EDLSupport) {
      this.EDLMaterial = new EDLMaterial(
        this.width,
        this.height,
        this.camera.near,
        this.camera.far,
        this.pixelRatio
      );

      this.EDLRenderTarget = new EDLRenderTarget(this.width, this.height,
        this.isWebGL2);
      this.addTarget(this.EDLRenderTarget);
    } else {
      this.EDLMaterial = null;
      this.EDLRenderTarget = null;

      console.log("EDL Not Supported");
    }
  }

  update() {
    this.updateEDLValues();
    this.resizeTextures();
    this.renderer.clear();
  }

  addTarget(target) {
    this.targets.push(target);
  }

  setCamera(camera) {
    this.camera = camera;
  }

  /** @private */
  resizeTextures() {
    let width = this.width;
    let height = this.height;

    this.targets.forEach(target => {
      if (!target) {
        return;
      }

      let w = target.width;
      let h = target.height;
      if ((w !== width) || (h !== height)) {
        target.dispose();
        target.setSize(width, height);
      }
    });
  }

  /** @private */
  updateEDLValues() {
    if (!this.EDLMaterial) {
      return;
    }

    this.EDLMaterial.uniforms.far.value = this.camera.far;
    this.EDLMaterial.uniforms.screenWidth.value = this.width;
    this.EDLMaterial.uniforms.screenHeight.value = this.height;
  }

  updateMaterial(material, values) {
    let keys = Object.keys(values);
    keys.forEach(key => {
      material[key] = values[key];
    });
  }

  setRenderTarget(target, clear = false) {
    this.renderer.setRenderTarget(target);
    if (clear) {
      this.renderer.clear();
    }
  }

  renderToScreen(material, target = null) {
    this.screenPass.render(this.renderer, material, target);
  }

  renderScenes(scenes, opacity, clear=true) {
    const toRender = scenes.filter(scene => scene !== null);
    if (toRender.length === 0) return;

    let numChildren = 0;
    toRender.forEach(scene => {
      const children = scene.children
        .filter(x => !(x instanceof AmbientLight))
        .filter(x => x.visible);
      numChildren += children.length;
    });

    if (numChildren === 0) return;

    this.setRenderTarget(this.renderTarget, clear);

    toRender.forEach(scene => {
      this.renderer.render(scene, this.camera);
    });

    this.updateMaterial(this.renderMaterial, {
      ...this.renderTargetAttrs, opacity: opacity
    });

    this.renderToScreen(this.renderMaterial);
  }
}